import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Appointment } from "../../types/appointment.definition";
import {
  defaultSearchRequest,
  SearchRequest,
} from "../../types/search.definition";

interface AppointmentState {
  appointments: Appointment[];
  loading: boolean;
  error: string | null;
  facilityId: number | null;
  // Filters
  searchTerm: string;
  filterStatus: string;
  filterType: string;
  selectedDate: string | null; // Store as ISO string for Redux serialization
  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  // Search request
  searchRequest: SearchRequest;
  // UI state
  showFilters: boolean;
  showAddModal: boolean;
  showCancelModal: boolean;
  appointmentToCancel: Appointment | null;
  isUpdating: string | null;
  isCreating: boolean;
}

const initialState: AppointmentState = {
  appointments: [],
  loading: false,
  error: null,
  facilityId: null,
  searchTerm: "",
  filterStatus: "",
  filterType: "",
  selectedDate: null,
  currentPage: 1,
  itemsPerPage: 10,
  totalItems: 0,
  searchRequest: {
    ...defaultSearchRequest,
    paginationSettings: {
      limit: 10,
      offset: 0,
    },
  },
  showFilters: false,
  showAddModal: false,
  showCancelModal: false,
  appointmentToCancel: null,
  isUpdating: null,
  isCreating: false,
};

const appointmentSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    fetchAppointmentsRequest: (
      state,
      action: PayloadAction<{
        facilityId: number;
        searchRequest: SearchRequest;
      }>
    ) => {
      // Only set loading if not already loading to prevent race conditions
      if (!state.loading) {
        state.loading = true;
        state.error = null;
      }
      state.facilityId = action.payload.facilityId;
      // Don't update searchRequest here to avoid triggering re-renders
    },
    fetchAppointmentsSuccess: (
      state,
      action: PayloadAction<{
        appointments: Appointment[];
        totalItems: number;
        paginationSettings: { limit: number; offset: number };
      }>
    ) => {
      state.appointments = action.payload.appointments;
      state.totalItems = action.payload.totalItems;
      state.searchRequest.paginationSettings =
        action.payload.paginationSettings;
      state.loading = false;
      state.error = null;
    },
    fetchAppointmentsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when search changes
    },
    setFilterStatus: (state, action: PayloadAction<string>) => {
      state.filterStatus = action.payload;
      state.currentPage = 1; // Reset to first page when filter changes
    },
    setFilterType: (state, action: PayloadAction<string>) => {
      state.filterType = action.payload;
      state.currentPage = 1; // Reset to first page when filter changes
    },
    setSelectedDate: (state, action: PayloadAction<Date | string | null>) => {
      state.selectedDate =
        action.payload instanceof Date
          ? action.payload.toISOString()
          : action.payload;
      state.currentPage = 1; // Reset to first page when date changes
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
      state.searchRequest.paginationSettings.offset =
        (action.payload - 1) * state.itemsPerPage;
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
      state.currentPage = 1; // Reset to first page when items per page changes
      state.searchRequest.paginationSettings.limit = action.payload;
      state.searchRequest.paginationSettings.offset = 0;
    },
    updateAppointmentInList: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Appointment> }>
    ) => {
      const index = state.appointments.findIndex(
        (apt) => apt.id === action.payload.id
      );
      if (index !== -1) {
        state.appointments[index] = {
          ...state.appointments[index],
          ...action.payload.updates,
        };
      }
    },
    addAppointmentToList: (state, action: PayloadAction<Appointment>) => {
      state.appointments.unshift(action.payload);
      state.totalItems += 1;
    },
    removeAppointmentFromList: (state, action: PayloadAction<string>) => {
      state.appointments = state.appointments.filter(
        (apt) => apt.id !== action.payload
      );
      state.totalItems = Math.max(0, state.totalItems - 1);
    },
    setShowFilters: (state, action: PayloadAction<boolean>) => {
      state.showFilters = action.payload;
    },
    setShowAddModal: (state, action: PayloadAction<boolean>) => {
      state.showAddModal = action.payload;
    },
    setShowCancelModal: (state, action: PayloadAction<boolean>) => {
      state.showCancelModal = action.payload;
    },
    setAppointmentToCancel: (
      state,
      action: PayloadAction<Appointment | null>
    ) => {
      state.appointmentToCancel = action.payload;
    },
    setIsUpdating: (state, action: PayloadAction<string | null>) => {
      state.isUpdating = action.payload;
    },
    setIsCreating: (state, action: PayloadAction<boolean>) => {
      state.isCreating = action.payload;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.filterStatus = "";
      state.filterType = "";
      state.selectedDate = null;
      state.currentPage = 1;
    },
    clearAppointments: (state) => {
      state.appointments = [];
      state.totalItems = 0;
      state.error = null;
    },
  },
});

export const {
  fetchAppointmentsRequest,
  fetchAppointmentsSuccess,
  fetchAppointmentsFailure,
  setSearchTerm,
  setFilterStatus,
  setFilterType,
  setSelectedDate,
  setCurrentPage,
  setItemsPerPage,
  updateAppointmentInList,
  addAppointmentToList,
  removeAppointmentFromList,
  setShowFilters,
  setShowAddModal,
  setShowCancelModal,
  setAppointmentToCancel,
  setIsUpdating,
  setIsCreating,
  resetFilters,
  clearAppointments,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
